import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ReactNode, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
});

const mapDispatchToProps = {
    updateProductInfo,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    labelsVisible?: boolean;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    extendedStyles?: ProductCarouselProductCondensedStyles;
    condensedActionsTemplate?: (product: ProductModel) => ReactNode;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductCondensedStyles {
    container?: GridContainerProps;
    imageGridItem?: GridItemProps;
    productImageStyles?: ProductImageStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    condensedActionsTemplateGridItem?: GridItemProps;
    productNumberCss?: InjectableCss;
}

export const productCarouselProductCondensedStyles: ProductCarouselProductCondensedStyles = {
    container: {
        gap: 4,
        css: css`
            height: 78px;
            width: 74px;
            display: flex;
            div[class^="GridOffset"] {
                margin: 4px 0;
                div[class^="GridItemStyle"] {
                    padding: 0;
                }
            }
        `,
    },
    imageGridItem: {
        width: 3,
        css: css`
            max-width: 100%;
            flex-basis: auto !important;
            height: 80px;
            border: 1px solid #e0e0e0;
            a[class^="StyledButton"] {
                height: 100%;
                padding: 5px;
                > div {
                    > img {
                        height: 91px;
                        width: 75px;
                    }
                }
            }
        `,
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin-bottom: 6px;
            `,
        },
    },
    condensedActionsTemplateGridItem: {
        width: 1,
        css: css`
            top: 1px;
            left: 55px;
            position: absolute;
            button[class^="ButtonWrapper"] {
                background: transparent;
                border: none;
            }
            span[class^="IconWrapper"] {
                height: 15px;
                width: 15px;
                color: #979797;
                > svg {
                    stroke-width: 3;
                }
            }
        `,
    },

    productNumberCss: {
        css: css`
            font-size: 10px;
            font-weight: 500;
            letter-spacing: 0;
            line-height: 12px;
            text-align: center;
            color: #006bc0;
        `,
    },
};

const ProductCarouselProductCondensed = ({
    carouselId,
    product,
    showImage,
    showPartNumbers,
    updateProductInfo,
    extendedStyles,
    productInfo,
    condensedActionsTemplate,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductCondensedStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    return (
        <>
            <ProductContext.Provider value={productContext}>
                <GridContainer {...styles.container}>
                    <GridItem {...styles.imageGridItem}>
                        {showImage && (
                            <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                        )}
                        {condensedActionsTemplate ? (
                            <StyledWrapper {...styles.condensedActionsTemplateGridItem}>
                                {condensedActionsTemplate(product)}
                            </StyledWrapper>
                        ) : (
                            []
                        )}
                    </GridItem>
                    <StyledWrapper
                        css={css`
                            margin: 0 auto;
                        `}
                    >
                        {showPartNumbers && (
                            <StyledWrapper {...styles.productNumberCss}>
                                {translate("Model#")} {product.productNumber}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </GridContainer>
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProductCondensed);
